import { Resource, Observation } from '@medplum/fhirtypes'
import { formatDateTime } from '@/utils'
import { useEffect, useState } from 'react'
import { useMedplum } from '@medplum/react'

type Props = {
  resource?: Resource
}

const availFields = [
  { code: 'date', title: 'Date' },
  { code: 'performer', title: 'Performer' },
  { code: 'result', title: 'Vital / Result', extraCode: 'interpreter' },
]

// A UCUM annotation unit like `{score}` (e.g. pain 0–10) is a bare label, not
// a printable unit — suppress it so a pain reading shows "5", not "5 {score}".
const displayUnit = (unit: string | undefined): string =>
  unit && !unit.startsWith('{') ? ` ${unit}` : ''

const getValue = (observation: Observation): string => {
  if (observation.valueQuantity) {
    return `${observation.valueQuantity.value}${displayUnit(observation.valueQuantity.unit)}`
  } else if (observation.valueString) {
    return observation.valueString
  } else if (observation.valueCodeableConcept) {
    return (
      observation.valueCodeableConcept.text ||
      observation.valueCodeableConcept.coding?.[0]?.display ||
      '-'
    )
  } else if (observation.valueBoolean !== undefined) {
    return observation.valueBoolean ? 'Yes' : 'No'
  } else {
    return '-'
  }
}

const renderColumn = (observation: Observation, itemCode: string): string => {
  switch (itemCode) {
    case 'date':
      return observation.effectiveDateTime
        ? formatDateTime(observation.effectiveDateTime, '-', true)
        : '-'

    case 'performer':
      return observation.performer?.[0]?.display || '-'

    case 'result':
      return (
        observation.hasMember
          ?.map((item) => (item.resource as Observation)?.code?.coding?.[0].display || '-')
          .join('\n') || '-'
      )

    case 'interpreter':
      return (
        observation.hasMember?.map((item) => getValue(item.resource as Observation)).join('\n') ||
        '-'
      )
    default:
      return itemCode
  }
}

export function Vitals({ resource }: Props) {
  const [observation, setObservation] = useState<Observation>()
  const medplum = useMedplum()

  useEffect(() => {
    if (!resource) return
    medplum
      .search('Observation', `_id=${resource.id}&_include=Observation:has-member`)
      .then((res) => {
        const list = res.entry?.map((entry) => entry.resource as Observation) || []
        const included = list.filter((item) =>
          item.code?.coding?.every((c) => c.code !== '85353-1'),
        )

        const group = resource as Observation
        const hasMember = []

        for (const member of group.hasMember || []) {
          let resource = included.find((item) => item.id === member.reference?.split('/')?.pop())
          if (!resource) {
            resource = group.contained?.find(
              (item) => item.id === member.reference?.split('/')?.pop(),
            ) as Observation | undefined
          }
          if (resource) {
            hasMember.push({
              ...member,
              resource,
            })
          }
        }

        group.hasMember = hasMember
        setObservation(group)
      })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resource])

  if (!observation) {
    return null
  }

  return (
    <div className="border-b-1 border-b-gray-200 pb-2">
      <table className="text-base">
        <tbody>
          {availFields.map((item) => (
            <tr>
              <td className="py-1 text-sm font-bold text-[#7D7987]">{item.title}:</td>
              <td className="px-[8px] py-1 text-base text-sm">
                <div>{renderColumn(observation, item.code)}</div>
                {!!item.extraCode && (
                  <div className="font-semibold">{renderColumn(observation, item.extraCode)}</div>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}
